import {
  useIsInsideRoom,
  useLostConnectionListener,
  useStatus,
} from "@liveblocks/react"
import { useCallback, useEffect, useRef } from "react"

import { useLiveblocksError } from "./LiveblocksErrorProvider"

export function RoomConnectionObserver() {
  const isInsideRoom = useIsInsideRoom()

  if (!isInsideRoom) return null

  return <RoomConnectionObserverContent />
}

function RoomConnectionObserverContent() {
  const status = useStatus()

  const { setError } = useLiveblocksError()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const clearObserverTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const updateTimeout = useCallback(
    (args: { message: string }) => {
      const { message } = args

      timeoutRef.current = setTimeout(() => {
        setError({
          type: "connection",
          message,
        })
      }, RECONNECTION_TIMEOUT)
    },
    [setError],
  )

  // Check the status before the room has fully established connection
  // Start timeout when connecting if not already started
  // Clear timeout when status is not "connecting"
  useEffect(() => {
    if (status === "connecting") {
      if (!timeoutRef.current) {
        updateTimeout({
          message:
            "Connection lost and wasn't restored within the reconnection window.",
        })
      }
    } else {
      clearObserverTimeout()
    }
  }, [status, clearObserverTimeout, updateTimeout])

  // Clear timeout on unmount
  useEffect(() => clearObserverTimeout, [clearObserverTimeout])

  // Handle lost connection events after having initially been connected to a room
  useLostConnectionListener((event) => {
    // Clear any existing timeouts when we get a new event
    clearObserverTimeout()

    switch (event) {
      case "lost":
        // Set a timeout to show error if connection isn't restored within the reconnection window
        updateTimeout({
          message:
            "Connection lost and wasn't restored within the reconnection window.",
        })

        break

      case "restored":
        break

      case "failed":
        setError({
          type: "connection",
          message: "Failed to restore connection.",
        })
        break
    }
  })

  return null
}

/**
 *  10 seconds timeout for reconnection
 *
 *  Liveblocks have a 30 to 120 seconds reconnection window.
 *  We use a shorter timeout to ensure users can move forward.
 */
const RECONNECTION_TIMEOUT = 10_000
